/**
 * Orchestra AI Platform - Workflow Builder Store
 * 
 * Zustand store for managing workflow builder state
 * 
 * @fileoverview Workflow builder state management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * @typedef {Object} WorkflowState
 * @property {Object[]} nodes - Workflow nodes
 * @property {Object[]} edges - Workflow edges
 * @property {string|null} selectedNodeId - Currently selected node ID
 * @property {Object} viewport - Canvas viewport state
 * @property {boolean} isExecuting - Whether workflow is executing
 * @property {Object} executionResults - Execution results
 * @property {Object[]} history - Undo/redo history
 * @property {number} historyIndex - Current history index
 */

/**
 * Create workflow store
 */
export const useWorkflowStore = create(
  devtools(
    (set, get) => ({
      // State
      nodes: [],
      edges: [],
      selectedNodeId: null,
      viewport: { x: 0, y: 0, zoom: 1 },
      isExecuting: false,
      executionResults: {},
      history: [],
      historyIndex: -1,

      // Actions
      /**
       * Set nodes
       * @param {Object[]} nodes - New nodes array
       */
      setNodes: (nodes) => {
        set({ nodes }, false, 'setNodes');
        get().saveToHistory();
      },

      /**
       * Set edges
       * @param {Object[]} edges - New edges array
       */
      setEdges: (edges) => {
        set({ edges }, false, 'setEdges');
        get().saveToHistory();
      },

      /**
       * Add node
       * @param {Object} node - Node to add
       */
      addNode: (node) => {
        const nodes = [...get().nodes, node];
        set({ nodes }, false, 'addNode');
        get().saveToHistory();
      },

      /**
       * Update node
       * @param {string} nodeId - Node ID
       * @param {Object} updates - Updates to apply
       */
      updateNode: (nodeId, updates) => {
        const nodes = get().nodes.map((node) =>
          node.id === nodeId ? { ...node, ...updates } : node
        );
        set({ nodes }, false, 'updateNode');
        get().saveToHistory();
      },

      /**
       * Delete node
       * @param {string} nodeId - Node ID to delete
       */
      deleteNode: (nodeId) => {
        const nodes = get().nodes.filter((node) => node.id !== nodeId);
        const edges = get().edges.filter(
          (edge) => edge.source !== nodeId && edge.target !== nodeId
        );
        set({ nodes, edges }, false, 'deleteNode');
        get().saveToHistory();
      },

      /**
       * Add edge
       * @param {Object} edge - Edge to add
       */
      addEdge: (edge) => {
        const edges = [...get().edges, edge];
        set({ edges }, false, 'addEdge');
        get().saveToHistory();
      },

      /**
       * Delete edge
       * @param {string} edgeId - Edge ID to delete
       */
      deleteEdge: (edgeId) => {
        const edges = get().edges.filter((edge) => edge.id !== edgeId);
        set({ edges }, false, 'deleteEdge');
        get().saveToHistory();
      },

      /**
       * Select node
       * @param {string|null} nodeId - Node ID to select
       */
      selectNode: (nodeId) => {
        set({ selectedNodeId: nodeId }, false, 'selectNode');
      },

      /**
       * Set viewport
       * @param {Object} viewport - Viewport state
       */
      setViewport: (viewport) => {
        set({ viewport }, false, 'setViewport');
      },

      /**
       * Set execution state
       * @param {boolean} isExecuting - Execution state
       */
      setExecuting: (isExecuting) => {
        set({ isExecuting }, false, 'setExecuting');
      },

      /**
       * Set execution results
       * @param {Object} results - Execution results
       */
      setExecutionResults: (results) => {
        set({ executionResults: results }, false, 'setExecutionResults');
      },

      /**
       * Save current state to history
       */
      saveToHistory: () => {
        const state = get();
        const snapshot = {
          nodes: JSON.parse(JSON.stringify(state.nodes)),
          edges: JSON.parse(JSON.stringify(state.edges)),
        };

        const history = state.history.slice(0, state.historyIndex + 1);
        history.push(snapshot);
        
        // Limit history to 50 entries
        const limitedHistory = history.slice(-50);
        
        set({
          history: limitedHistory,
          historyIndex: limitedHistory.length - 1,
        }, false, 'saveToHistory');
      },

      /**
       * Undo last action
       */
      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex > 0) {
          const previousState = history[historyIndex - 1];
          set({
            nodes: previousState.nodes,
            edges: previousState.edges,
            historyIndex: historyIndex - 1,
          }, false, 'undo');
        }
      },

      /**
       * Redo last undone action
       */
      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextState = history[historyIndex + 1];
          set({
            nodes: nextState.nodes,
            edges: nextState.edges,
            historyIndex: historyIndex + 1,
          }, false, 'redo');
        }
      },

      /**
       * Reset store
       */
      reset: () => {
        set({
          nodes: [],
          edges: [],
          selectedNodeId: null,
          viewport: { x: 0, y: 0, zoom: 1 },
          isExecuting: false,
          executionResults: {},
          history: [],
          historyIndex: -1,
        }, false, 'reset');
      },

      /**
       * Load workflow
       * @param {Object} workflowData - Workflow data to load
       */
      loadWorkflow: (workflowData) => {
        set({
          nodes: workflowData.nodes || [],
          edges: workflowData.edges || [],
          viewport: workflowData.viewport || { x: 0, y: 0, zoom: 1 },
        }, false, 'loadWorkflow');
        get().saveToHistory();
      },
    }),
    { name: 'WorkflowStore' }
  )
);

